import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export interface PowerBandCost {
  kwh: number
  cost: number
}

export interface PowerPeriod {
  kwh: number
  cost: number
  energy_cost: number
  by_band: Record<string, PowerBandCost>
  elapsed_fraction?: number
}

export interface PowerSummary {
  source: string
  currency: string
  online: boolean
  last_seen: string | null
  power_w: number | null
  today: PowerPeriod
  month: PowerPeriod
  // month_kwh/month_cost are null early in the month, when too little has
  // elapsed for the extrapolation to mean anything.
  projection: { month_kwh: number | null; month_cost: number | null }
  tariff: {
    option: string
    rate_hp: number | null
    rate_hc: number | null
    rate_base: number | null
    hc_windows: { start: string; end: string }[]
  }
}

export interface PowerTariff {
  option: string
  currency: string
  tz: string
  rate_base: number | null
  rate_hp: number | null
  rate_hc: number | null
  hc_windows: { start: string; end: string }[]
  tempo_rates: Record<string, number> | null
}

export interface PowerSeries {
  range: string
  bucket_seconds: number
  source: string
  points: { b: string; power_w: number | null; online: boolean }[]
}

export function usePower() {
  return useQuery<PowerSummary>({
    queryKey: ['power'],
    queryFn: () => api<PowerSummary>('/api/metrics/power'),
    refetchInterval: 15_000,
  })
}

export function usePowerSeries(range = '24h') {
  return useQuery<PowerSeries>({
    queryKey: ['power-series', range],
    queryFn: () => api<PowerSeries>(`/api/metrics/power/series?range=${range}`),
    refetchInterval: 30_000,
  })
}

export interface PowerHistoryPoint {
  bucket: string
  kwh: number
  kwh_hp: number
  kwh_hc: number
  cost: number
}

export interface PowerHistory {
  range: string
  granularity: 'hour' | 'day' | 'month'
  currency: string
  points: PowerHistoryPoint[]
  total_kwh: number
  total_cost: number
}

export type HistoryRange = '24h' | '7d' | '30d' | 'month' | 'year'

export function usePowerHistory(range: HistoryRange) {
  return useQuery<PowerHistory>({
    queryKey: ['power-history', range],
    queryFn: () => api<PowerHistory>(`/api/metrics/power/history?range=${range}`),
    refetchInterval: 60_000,
  })
}

export function usePowerTariff() {
  return useQuery<PowerTariff>({
    queryKey: ['power-tariff'],
    queryFn: () => api<PowerTariff>('/api/metrics/power/tariff'),
  })
}

export function useUpdateTariff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<PowerTariff>) =>
      api<PowerTariff>('/api/metrics/power/tariff', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['power-tariff'] })
      qc.invalidateQueries({ queryKey: ['power'] })
    },
  })
}

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' }

export function money(n: number | null | undefined, currency = 'EUR'): string {
  if (n == null) return '—'
  const sym = CURRENCY_SYMBOL[currency] ?? currency
  return `${n.toFixed(2)} ${sym}`
}

export function watts(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n >= 1000) return `${(n / 1000).toFixed(2)} kW`
  return `${n.toFixed(0)} W`
}

export function kwh(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toFixed(n < 10 ? 2 : 1)} kWh`
}
